import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useForm } from "react-hook-form";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useCustomer from "common/datahooks/useCustomer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { FullNameSchema } from "../../schema";
import SettingsItemContainer from "../SettingsItemContainer";

export default function FullName({
  isEditable,
  isEditing,
  startEditing,
  closeEditing,
}: {
  isEditable: boolean;
  isEditing: boolean;
  startEditing: () => void;
  closeEditing: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const { customerInfo, updateCustomer, isCustomerUpdating } = useCustomer();
  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    firstName: string;
    lastName: string;
  }>({
    resolver: valibotResolver(FullNameSchema),
    defaultValues: {
      firstName: customerInfo.first_name,
      lastName: customerInfo.last_name,
    },
  });

  async function onSubmit({
    firstName,
    lastName,
  }: {
    firstName: string;
    lastName: string;
  }) {
    if (
      firstName !== customerInfo.first_name ||
      lastName !== customerInfo.last_name
    ) {
      await updateCustomer({ firstName, lastName });
    }
    closeEditing();
  }
  function onCancel() {
    closeEditing();
    reset({
      firstName: customerInfo.first_name,
      lastName: customerInfo.last_name,
    });
  }

  return (
    <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
      <SettingsItemContainer
        className={clsx(
          "items-center gap-2 lg:flex-row",
          isEditing && "flex-col",
        )}
      >
        {isEditing ? (
          <>
            <div className="flex w-full gap-2 flex-col lg:flex-row">
              <FormField
                label="First name"
                htmlFor="firstName"
                size={isDesktop ? "sm" : "lg"}
                className="flex-1"
                error={errors.firstName?.message}
              >
                <Input
                  placeholder="First name"
                  id="firstName"
                  autoFocus
                  variant={isDesktop ? "sm" : "lg"}
                  {...register("firstName")}
                />
              </FormField>
              <FormField
                label="Last name"
                htmlFor="lastName"
                size={isDesktop ? "sm" : "lg"}
                className="flex-1"
                error={errors.lastName?.message}
              >
                <Input
                  placeholder="Last name"
                  id="lastName"
                  variant={isDesktop ? "sm" : "lg"}
                  {...register("lastName")}
                />
              </FormField>
            </div>
            <div className="flex gap-x-2 mt-auto ml-auto">
              <Button
                variant="secondary-black"
                size={isDesktop ? "sm" : "md"}
                onClick={onCancel}
                disabled={isCustomerUpdating}
              >
                Cancel
              </Button>
              <Button
                size={isDesktop ? "sm" : "md"}
                type="submit"
                variant="secondary-purple"
                isLoading={isCustomerUpdating}
              >
                Save changes
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-y-0.5">
              <span className="text-sm-bold">Full name</span>
              <span className="text-black-500 text-xs-medium">
                {customerInfo.first_name} {customerInfo.last_name}
              </span>
            </div>
            <RenderIf condition={isEditable}>
              <Button
                variant="ghost-black"
                intent="iconOnly"
                className="ml-auto"
                onClick={startEditing}
              >
                <PencilSimpleLine />
              </Button>
            </RenderIf>
          </>
        )}
      </SettingsItemContainer>
    </form>
  );
}
